import { create } from 'zustand';
import { getRefills, postRefills } from '../services/refillsApi';
import { setSessionUser } from '../utils/session';
import type {
  DeliveryType,
  PaymentType,
  PickupLocation,
  Refill,
  RefillsStore,
  RefillSubmitPayload,
} from './types';

const MAIL_ORDER_DELIVERY_TYPE_ID = 10001;
const PICKUP_DELIVERY_TYPE_ID = 10002;

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
}

function mapDeliveryType(value: unknown): DeliveryType {
  const source = asRecord(value);
  return {
    DeliveryTypeId: asNumber(source.DeliveryTypeId, 0),
    DeliveryTypeName: asString(source.DeliveryTypeName, ''),
    PickupLocations: asArray(source.PickupLocations).map(mapPickupLocation),
    PaymentTypes: asArray(source.PaymentTypes).map(mapPaymentType),
  };
}

function mapPickupLocation(value: unknown): PickupLocation {
  const source = asRecord(value);
  return {
    PickupLocationId: asNumber(source.PickupLocationId, 0),
    PickupLocationName: asString(source.PickupLocationName, ''),
    ...source,
  };
}

function mapPaymentType(value: unknown): PaymentType {
  const source = asRecord(value);
  return {
    PaymentTypeId: asNumber(source.PaymentTypeId, 0),
    PaymentTypeName: asString(source.PaymentTypeName, ''),
    ...source,
  };
}

function mapRefill(value: unknown): Refill {
  const source = asRecord(value);
  const copayAmt = asNumber(source.CopayAmt, 0);
  const numRefillsLeft = asNumber(source.NumRefillsLeft, 0);
  const refillClass = asString(source.RefillClass, '');
  const mailTrackingUrl = asString(source.MailTrackingUrl, '');

  return {
    selected: false,
    rx: asString(source.ScriptNo, ''),
    name: asString(source.DrugName, ''),
    canSelect: asBoolean(source.IsRefillable, false),
    refillClass,
    copayAmt,
    unableToCalculate: copayAmt === -999,
    refillsLeft: numRefillsLeft < 0 ? 1 : numRefillsLeft,
    trackingAvailable: mailTrackingUrl.length > 0,
    raw: source,
  };
}

function computeSelection(refills: Refill[]): {
  selectedRefills: Refill[];
  countPrice: number;
} {
  const selectedRefills = refills.filter(item => item.selected);
  const countPrice = selectedRefills.reduce((sum, item) => {
    if (item.unableToCalculate) {
      return sum;
    }
    return sum + item.copayAmt;
  }, 0);

  return {
    selectedRefills,
    countPrice,
  };
}

function getMethodById(methods: DeliveryType[], methodId: number): DeliveryType | null {
  return methods.find(method => method.DeliveryTypeId === methodId) || null;
}

function getLocationById(locations: PickupLocation[], locationId: number): PickupLocation | null {
  return locations.find(location => location.PickupLocationId === locationId) || null;
}

function getPaymentById(payments: PaymentType[], paymentId: number): PaymentType | null {
  return payments.find(payment => payment.PaymentTypeId === paymentId) || null;
}

function buildSubmitPayload(
  state: RefillsStore,
  payload?: RefillSubmitPayload
): RefillSubmitPayload | null {
  if (payload) {
    const normalized = { ...payload };
    if (normalized.DeliveryTypeId === PICKUP_DELIVERY_TYPE_ID) {
      delete normalized.AddressTypeId;
    }
    return normalized;
  }

  if (
    !state.linkToken ||
    !state.deliveryMethod ||
    !state.paymentType ||
    state.selectedRefills.length < 1
  ) {
    return null;
  }

  const submitPayload: RefillSubmitPayload = {
    LinkToken: state.linkToken,
    DeliveryTypeId: state.deliveryMethod.DeliveryTypeId,
    PickupLocationId:
      state.deliveryMethod.DeliveryTypeId === PICKUP_DELIVERY_TYPE_ID
        ? state.pickupLocation?.PickupLocationId || 0
        : -1,
    PaymentTypeId: state.paymentType.PaymentTypeId,
    CheckAllScripts: 0,
    ScriptNos: state.selectedRefills.map(item => item.rx),
  };

  if (
    state.deliveryMethod.DeliveryTypeId === MAIL_ORDER_DELIVERY_TYPE_ID &&
    state.shippingAddressId
  ) {
    submitPayload.AddressTypeId = state.shippingAddressId;
  }

  return submitPayload;
}

const initialState = {
  refills: [] as import('./types').Refill[],
  selectedRefills: [] as import('./types').Refill[],
  deliveryMethod: null as import('./types').DeliveryType | null,
  availableDeliveryTypes: [] as import('./types').DeliveryType[],
  pickupLocation: null as import('./types').PickupLocation | null,
  availableLocations: [] as import('./types').PickupLocation[],
  paymentType: null as import('./types').PaymentType | null,
  availablePayments: [] as import('./types').PaymentType[],
  shippingAddressId: null as number | null,
  linkToken: null as string | null,
  confirmUserName: null as string | null,
  ccExplanation: null as string | null,
  countPrice: 0,
  isMailOrder: false,
  isPatientAutoRefillAllowed: false,
  isPatientAutoRefillEnabled: false,
  loading: false,
  noScripts: false,
  error: null as string | null,
  submitSuccess: false,
};

export const useRefillsStore = create<RefillsStore>((set, get) => ({
  ...initialState,

  loadRefills: async (linkToken, deliveryId, locationId, paymentId, options) => {
    const silent = options?.silent ?? false;
    set({ loading: !silent, error: null, noScripts: false });

    try {
      const response = await getRefills({
        linkToken,
        deliveryTypeId: deliveryId,
        pickupLocationId: locationId,
        paymentTypeId: paymentId,
      });

      const source = asRecord(response);
      const methods = asArray(source.DeliveryTypes).map(mapDeliveryType);
      const method =
        getMethodById(methods, deliveryId || asNumber(source.DeliveryTypeId, 0)) ||
        methods[0] ||
        null;
      const locations = method?.PickupLocations || [];
      const payments = method?.PaymentTypes || [];
      const location =
        getLocationById(locations, locationId || asNumber(source.PickupLocationId, 0)) ||
        locations[0] ||
        null;
      const payment =
        getPaymentById(payments, paymentId || asNumber(source.PaymentTypeId, 0)) ||
        payments[0] ||
        null;
      const refills = asArray(source.Prescriptions).map(mapRefill);
      const selection = computeSelection(refills);
      const firstName = asString(source.FirstName, '').trim();
      const lastName = asString(source.LastName, '').trim();
      const confirmUserName = `${firstName} ${lastName}`.trim() || null;
      const ccExplanationValue = source.CreditCardExplanation;
      const ccExplanation = typeof ccExplanationValue === 'string' ? ccExplanationValue : null;

      set({
        linkToken: asString(source.LinkToken, linkToken),
        confirmUserName,
        ccExplanation,
        availableDeliveryTypes: methods,
        deliveryMethod: method,
        availableLocations: locations,
        pickupLocation: location,
        availablePayments: payments,
        paymentType: payment,
        isMailOrder: method?.DeliveryTypeId === MAIL_ORDER_DELIVERY_TYPE_ID,
        isPatientAutoRefillAllowed: asBoolean(source.IsPatientAutoRefillAllowed, false),
        isPatientAutoRefillEnabled: asBoolean(source.IsPatientAutoRefillEnabled, false),
        refills,
        selectedRefills: selection.selectedRefills,
        countPrice: selection.countPrice,
        loading: false,
        noScripts: refills.length < 1,
      });
    } catch (error: unknown) {
      set({
        loading: false,
        noScripts: true,
        error: normalizeErrorMessage(error, 'Unable to load refills right now. Please try again.'),
      });
    }
  },

  toggleScript: rx => {
    const nextRefills = get().refills.map(item => {
      if (item.rx !== rx.rx || !item.canSelect) {
        return item;
      }

      return {
        ...item,
        selected: !item.selected,
      };
    });

    const selection = computeSelection(nextRefills);

    set({
      refills: nextRefills,
      selectedRefills: selection.selectedRefills,
      countPrice: selection.countPrice,
      error: null,
    });
  },

  selectAll: selected => {
    const nextRefills = get().refills.map(item => ({
      ...item,
      selected: item.canSelect ? selected : item.selected,
    }));
    const selection = computeSelection(nextRefills);

    set({
      refills: nextRefills,
      selectedRefills: selection.selectedRefills,
      countPrice: selection.countPrice,
      error: null,
    });
  },

  setDeliveryMethod: method => {
    const availableLocations = method.PickupLocations || [];
    const availablePayments = method.PaymentTypes || [];
    set({
      deliveryMethod: method,
      availableLocations,
      pickupLocation: availableLocations[0] || null,
      availablePayments,
      paymentType: availablePayments[0] || null,
      isMailOrder: method.DeliveryTypeId === MAIL_ORDER_DELIVERY_TYPE_ID,
      shippingAddressId:
        method.DeliveryTypeId === MAIL_ORDER_DELIVERY_TYPE_ID ? get().shippingAddressId : null,
      error: null,
    });
  },

  setPickupLocation: location => {
    set({ pickupLocation: location, error: null });
  },

  setPaymentType: payment => {
    set({ paymentType: payment, error: null });
  },

  setShippingAddressId: id => {
    set({ shippingAddressId: id, error: null });
  },

  prepareConfirmOrder: () => {
    const state = get();

    if (state.selectedRefills.length < 1) {
      set({ error: 'You need to select at least one refill.' });
      return;
    }

    if (state.isMailOrder && !state.shippingAddressId) {
      set({ error: 'You need to select a shipping address for home delivery.' });
      return;
    }

    set({ error: null });
  },

  clearError: () => {
    set({ error: null });
  },

  submitConfirmOrder: async payload => {
    const state = get();
    state.prepareConfirmOrder();

    if (get().error) {
      return;
    }

    const submitPayload = buildSubmitPayload(get(), payload);
    if (!submitPayload) {
      set({ error: 'Unable to build confirm-order payload from current state.' });
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await postRefills(submitPayload);
      const statusCode = asString(asRecord(response).StatusCode, '');

      if (statusCode && statusCode !== 'SUCCESS') {
        throw new Error('There was a problem submitting your refill order.');
      }

      set({ submitSuccess: true, loading: false });
    } catch (error: unknown) {
      set({
        loading: false,
        error: normalizeErrorMessage(
          error,
          'Unable to submit your refill order right now. Please try again.'
        ),
      });
    }
  },

  resetOrder: () => {
    const resetRefills = get().refills.map(item => ({ ...item, selected: false }));

    set({
      refills: resetRefills,
      selectedRefills: [],
      countPrice: 0,
      error: null,
      submitSuccess: false,
    });
  },

  refreshAfterCardUpdate: updatedUser => {
    setSessionUser(updatedUser);
    set({ error: null });

    const state = get();
    if (!state.linkToken) {
      return;
    }

    void state.loadRefills(
      state.linkToken,
      state.deliveryMethod?.DeliveryTypeId,
      state.pickupLocation?.PickupLocationId,
      state.paymentType?.PaymentTypeId,
      { silent: true }
    );
  },

  reset: () => set(initialState),
}));
